import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  MouseEvent,
  useMemo,
  FC,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  Link as RouterLink,
} from 'react-router-dom';
import LinkIcon from 'core/icons/link';

type CommonComponentProps = {
  className?: string;
};

type CommonComponentType = FC<CommonComponentProps>;

type LinkProps = {
  to?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  Icon?: CommonComponentType;
};

type Part = 'root' | 'label' | 'textRoot' | 'link' | 'icon';

type Props = {
  className?: string;
  text: ReactNode | string;
  children?: ReactNode;
  href?: LinkProps | string;
  classes?: Partial<Record<Part, string>>;
  target?: '_blank' | '_self' | '_parent' | '_top';
};

const containerClasses = 'flex gap-2 items-center w-full group/link';

function Link({
  className,
  classes,
  text,
  href,
  children,
  target,
}: Props, ref: ForwardedRef<HTMLAnchorElement | HTMLDivElement>) {
  const isCustomLink = typeof href !== 'string';
  const to = isCustomLink ? href?.to ?? null : href;

  const hasChildren = !isNil(children);
  const isLink = !isNil(href);

  const rootClass = clsx(
    'relative w-full flex items-center gap-2 font-medium',
    className,
    classes?.root,
  );

  const labelClasses = clsx(
    'truncate',
    { 'group-hover/link:text-blue-500': isLink },
    classes?.label,
  );

  const IconLink = useMemo(() => (
    (isCustomLink && !isNil(href?.Icon) ? href!.Icon : LinkIcon) as CommonComponentType
  ), [href, isCustomLink]);

  return (
    <div className={rootClass}>
      <div className={clsx('flex flex-1 flex-col truncate', classes?.textRoot)}>
        {isLink
          ? (
            <RouterLink
              ref={ref as RefObject<HTMLAnchorElement>}
              to={to ?? ''}
              target={target}
              className={clsx(
                containerClasses,
                'rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-default-input-focused',
                classes?.link,
              )}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                if (isCustomLink && typeof href?.onClick === 'function') {
                  e.preventDefault();
                  href.onClick(e);
                }
              }}
            >
              <h2 className={labelClasses}>
                {text}
              </h2>
              <IconLink
                className={clsx(
                  '!text-sm group-hover/link:!text-blue-500',
                  { '!hidden group-hover/link:!block': isNil(classes?.icon) },
                  classes?.icon,
                )}
              />
            </RouterLink>
          )
          : (
            <div
              ref={ref as RefObject<HTMLDivElement>}
              className={containerClasses}
            >
              <h2 className={labelClasses}>
                {text}
              </h2>
            </div>
          )}
        {hasChildren && children}
      </div>
    </div>
  );
}

export default forwardRef(Link);
